import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Link,
  Text,
} from "@chakra-ui/react";
import { cafStockApi, emissionProfilesApi, foliosApi } from "api/endpoints";
import AppModal from "components/modal/AppModal";
import React from "react";
import { HiOutlineUpload } from "react-icons/hi";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import {
  labelTipoDte,
  parseCafXmlPreview,
  SII_SOLICITA_FOLIOS_URL,
} from "utils/cafXmlPreview";

/**
 * Modal: elegir CAF .xml, vista previa de TD / RUT / rango (leído del propio CAF) y subida.
 * El ambiente de validación/stock coincide con `dte_emission_profiles.ambiente` (misma fuente que DTE).
 */
export default function CafUploadModal({ isOpen, onClose, onUploaded }) {
  const fileInputRef = React.useRef(null);
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [parseError, setParseError] = React.useState("");
  const [uploadBusy, setUploadBusy] = React.useState(false);
  const [uploadError, setUploadError] = React.useState("");
  const [profilesForRut, setProfilesForRut] = React.useState([]);
  const [profilesLoadState, setProfilesLoadState] = React.useState("idle");
  const [pickedInternalId, setPickedInternalId] = React.useState("");
  const [activateNow, setActivateNow] = React.useState(true);
  const [repairMode, setRepairMode] = React.useState(false);
  const [allowMultipleActive, setAllowMultipleActive] = React.useState(false);
  const [replacePreviousActiveOnUpload, setReplacePreviousActiveOnUpload] = React.useState(false);
  const [forceAllowOlderRange, setForceAllowOlderRange] = React.useState(true);
  const [forceAllowSequenceRollback, setForceAllowSequenceRollback] = React.useState(true);
  const [replaceExistingActive, setReplaceExistingActive] = React.useState(true);
  const [forceReason, setForceReason] = React.useState("");
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
      profileInternalId: s.profileInternalId,
      rutEmisor: s.rutEmisor,
    })),
  );
  const activeProfileUuid =
    emission.kind === "ready" ? String(emission.profileUuid || "").trim() : "";
  const activeProfileInternalId =
    emission.kind === "ready" ? String(emission.profileInternalId || "").trim() : "";
  const activeProfileRut =
    emission.kind === "ready" ? String(emission.rutEmisor || "").trim() : "";
  const canUseRepairMode = true;

  const reset = React.useCallback(() => {
    setFile(null);
    setPreview(null);
    setParseError("");
    setUploadError("");
    setProfilesForRut([]);
    setProfilesLoadState("idle");
    setPickedInternalId("");
    setActivateNow(true);
    setRepairMode(false);
    setAllowMultipleActive(false);
    setReplacePreviousActiveOnUpload(false);
    setForceAllowOlderRange(true);
    setForceAllowSequenceRollback(true);
    setReplaceExistingActive(true);
    setForceReason("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleClose = React.useCallback(() => {
    if (uploadBusy) return;
    reset();
    onClose();
  }, [onClose, reset, uploadBusy]);

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  React.useEffect(() => {
    if (!isOpen || !preview?.ok || !preview.rutEmisor) {
      setProfilesForRut([]);
      setPickedInternalId("");
      setProfilesLoadState("idle");
      return;
    }
    let cancelled = false;
    (async () => {
      setProfilesLoadState("loading");
      try {
        const { res, data } = await emissionProfilesApi.list({
          rut_emisor: preview.rutEmisor,
          estado: "active",
        });
        if (cancelled) return;
        if (!res.ok) {
          setProfilesForRut([]);
          setProfilesLoadState("error");
          return;
        }
        const items = Array.isArray(data?.items) ? data.items : [];
        setProfilesForRut(items);
        const normalizedPreviewRut = String(preview.rutEmisor || "").trim().toUpperCase();
        const normalizedActiveRut = String(activeProfileRut || "").trim().toUpperCase();
        if (activeProfileInternalId && normalizedPreviewRut && normalizedPreviewRut === normalizedActiveRut) {
          setPickedInternalId(activeProfileInternalId);
        } else if (items.length === 1 && items[0]?.internal_id) {
          setPickedInternalId(String(items[0].internal_id));
        } else {
          setPickedInternalId("");
        }
        setProfilesLoadState("ready");
      } catch {
        if (!cancelled) {
          setProfilesForRut([]);
          setProfilesLoadState("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, preview?.ok, preview?.rutEmisor, activeProfileInternalId, activeProfileRut]);

  const readFile = (f) => {
    setFile(f);
    setParseError("");
    setUploadError("");
    setPreview(null);
    if (!f) return;
    if (!/\.xml$/i.test(f.name)) {
      setParseError("El archivo debe ser .xml (CAF del SII).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      const p = parseCafXmlPreview(text);
      if (!p.ok) {
        setParseError(p.error);
        return;
      }
      setPreview(p);
    };
    reader.onerror = () => {
      setParseError("No se pudo leer el archivo.");
    };
    reader.readAsText(f);
  };

  const canUseActiveProfile = Boolean(activeProfileUuid);
  const resolvedProfileUuid = canUseActiveProfile
    ? activeProfileUuid
    : "";
  const needsProfilePick =
    !resolvedProfileUuid && profilesForRut.length > 1 && !pickedInternalId.trim();
  const hasForceFlagsEnabled = Boolean(
    forceAllowOlderRange || forceAllowSequenceRollback || replaceExistingActive,
  );
  const requiresForceReason = repairMode && hasForceFlagsEnabled;
  const normalizedForceReason = forceReason.trim();
  const repairValidationError =
    requiresForceReason && !normalizedForceReason
      ? "Debes ingresar un motivo de reparación cuando activas flags forzados."
      : "";

  const ensureUsableSequence = React.useCallback(async ({
    profileUuid,
    tipoDte,
    ambiente,
  }) => {
    const qs = new URLSearchParams();
    qs.set("profileUuid", profileUuid);
    qs.set("tipo_dte", String(tipoDte));
    qs.set("estado_actual", "available");
    if (ambiente) qs.set("ambiente", ambiente);
    const first = await foliosApi.listSequences(`?${qs.toString()}`);
    const firstItems = Array.isArray(first?.data?.items) ? first.data.items : [];
    if (first?.res?.ok && first?.data?.ok && firstItems.length > 0) {
      return { ok: true, synced: false };
    }
    await foliosApi.syncSequences({ profileUuid });
    const second = await foliosApi.listSequences(`?${qs.toString()}`);
    const secondItems = Array.isArray(second?.data?.items) ? second.data.items : [];
    return {
      ok: Boolean(second?.res?.ok && second?.data?.ok && secondItems.length > 0),
      synced: true,
    };
  }, []);

  const handleUpload = async () => {
    if (!file || !preview?.ok || needsProfilePick || repairValidationError) return;
    setUploadBusy(true);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("caf", file);
      if (resolvedProfileUuid) {
        fd.append("profileUuid", resolvedProfileUuid);
      } else if (pickedInternalId.trim()) {
        fd.append("emissionProfileInternalId", pickedInternalId.trim());
      }
      if (activateNow) {
        fd.append("estado", "active");
      }
      if (allowMultipleActive) {
        fd.append("allowMultipleActive", "true");
      }
      if (replacePreviousActiveOnUpload) {
        fd.append("replaceExistingActive", "true");
        fd.append(
          "forceReason",
          "Reemplazo manual desde frontend: subir nuevo CAF aunque el folio/CAF activo anterior no se haya consumido.",
        );
      }
      if (repairMode) {
        if (forceAllowOlderRange) {
          fd.append("forceAllowOlderRange", "true");
        }
        if (forceAllowSequenceRollback) {
          fd.append("forceAllowSequenceRollback", "true");
        }
        if (replaceExistingActive) {
          fd.append("replaceExistingActive", "true");
        }
        if (normalizedForceReason) {
          fd.append("forceReason", normalizedForceReason);
        }
      }
      const { res, data } = await cafStockApi.uploadXml(fd);
      if (!res.ok || !data?.ok) {
        const msg =
          data?.error ||
          data?.message ||
          (res.status === 403
            ? "No tenés permiso para cargar CAF (manage_caf)."
            : `Error ${res.status}`);
        const maybeRangeError = /(secuencia|rango|CAF_OLDER|overlap|ambiente)/i.test(String(msg));
        setUploadError(
          maybeRangeError
            ? `${msg}\n\nTip: revisá que el profileUuid activo corresponda al ambiente correcto (certificación/producción).`
            : msg
        );
        return;
      }
      const uploadedTipoDte = Number(data?.cafSummary?.tipo_dte ?? preview.tipoDte ?? 0);
      const uploadedAmbiente = String(
        data?.cafSummary?.ambiente ?? emission.ambiente ?? "",
      ).trim();
      const uploadedProfileUuid = String(
        data?.cafSummary?.emission_profile_uuid ?? resolvedProfileUuid ?? "",
      ).trim();
      if (activateNow && uploadedProfileUuid && uploadedTipoDte > 0) {
        const sequenceCheck = await ensureUsableSequence({
          profileUuid: uploadedProfileUuid,
          tipoDte: uploadedTipoDte,
          ambiente: uploadedAmbiente,
        });
        if (!sequenceCheck.ok) {
          setUploadError(
            "CAF subido, pero no quedó una secuencia disponible para emitir en este perfil. "
            + "Revisá que el profileUuid corresponda al ambiente correcto.",
          );
          return;
        }
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("caf-uploaded"));
      }
      if (onUploaded) await onUploaded();
      handleClose();
    } catch (e) {
      setUploadError(
        e instanceof Error ? e.message : "Error al subir el CAF."
      );
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Subir CAF (XML SII)"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} isDisabled={uploadBusy}>
            Cancelar
          </Button>
          <Button
            colorScheme="brand"
            leftIcon={<HiOutlineUpload />}
            onClick={handleUpload}
            isLoading={uploadBusy}
            loadingText="Subiendo…"
            isDisabled={
              !file ||
              !preview?.ok ||
              profilesLoadState === "loading" ||
              needsProfilePick ||
              Boolean(repairValidationError)
            }
          >
            Confirmar subida
          </Button>
        </>
      }
    >
      <Text fontSize="sm" color="gray.600" className="dark:text-gray-400" mb={3}>
        El stock de CAF se valida con el mismo <strong>ambiente del perfil de emisión</strong> que
        usás para los DTE (certificación / producción). Si hay más de un perfil para este RUT,
        elegí cuál corresponde al XML antes de confirmar.
      </Text>

      <Checkbox
        isChecked={activateNow}
        onChange={(e) => setActivateNow(Boolean(e.target.checked))}
        isDisabled={uploadBusy || repairMode}
        mb={3}
      >
        Dejar CAF activo para emitir inmediatamente
      </Checkbox>

      {!repairMode ? (
        <>
          <Checkbox
            isChecked={allowMultipleActive}
            onChange={(e) => {
              const next = Boolean(e.target.checked);
              setAllowMultipleActive(next);
              if (next) {
                setReplacePreviousActiveOnUpload(false);
              }
            }}
            isDisabled={uploadBusy}
            mb={3}
          >
            Permitir múltiples CAF activos
          </Checkbox>

          <Checkbox
            isChecked={replacePreviousActiveOnUpload}
            onChange={(e) => {
              const next = Boolean(e.target.checked);
              setReplacePreviousActiveOnUpload(next);
              if (next) {
                setAllowMultipleActive(false);
              }
            }}
            isDisabled={uploadBusy}
            mb={3}
          >
            Subir aunque no se consumió el folio anterior, desactivará el folio activo anterior
          </Checkbox>
        </>
      ) : null}

      {resolvedProfileUuid ? (
        <Alert status="info" borderRadius="md" fontSize="sm" mb={3}>
          <AlertIcon />
          <AlertDescription>
            Usando profileUuid activo del emisor/ambiente actual. No se mezclan datos de otros ambientes.
          </AlertDescription>
        </Alert>
      ) : null}

      {canUseRepairMode ? (
        <Box
          rounded="lg"
          borderWidth="1px"
          borderColor={repairMode ? "orange.300" : "gray.200"}
          bg={repairMode ? "orange.50" : "gray.50"}
          p={4}
          mb={4}
          className={repairMode ? "dark:border-orange-300/40 dark:bg-orange-500/10" : "dark:border-white/10 dark:bg-white/5"}
        >
          <Checkbox
            isChecked={repairMode}
            onChange={(e) => {
              const next = Boolean(e.target.checked);
              setRepairMode(next);
              if (next) {
                setActivateNow(true);
                setAllowMultipleActive(true);
              }
            }}
            isDisabled={uploadBusy}
            mb={repairMode ? 3 : 0}
          >
            Modo reparación / forzar carga
          </Checkbox>

          {repairMode ? (
            <div className="space-y-3">
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <AlertDescription>
                  Esta acción puede alterar la secuencia operativa del CAF. Úsala solo para reparación o contingencia.
                </AlertDescription>
              </Alert>

              <div className="grid gap-2 sm:grid-cols-2">
                <Checkbox
                  isChecked={allowMultipleActive}
                  onChange={(e) => setAllowMultipleActive(Boolean(e.target.checked))}
                  isDisabled={uploadBusy}
                >
                  Permitir múltiples CAF activos
                </Checkbox>
                <Checkbox
                  isChecked={forceAllowOlderRange}
                  onChange={(e) => setForceAllowOlderRange(Boolean(e.target.checked))}
                  isDisabled={uploadBusy}
                >
                  Permitir rango anterior
                </Checkbox>
                <Checkbox
                  isChecked={forceAllowSequenceRollback}
                  onChange={(e) => setForceAllowSequenceRollback(Boolean(e.target.checked))}
                  isDisabled={uploadBusy}
                >
                  Permitir rollback de secuencia
                </Checkbox>
                <Checkbox
                  isChecked={replaceExistingActive}
                  onChange={(e) => setReplaceExistingActive(Boolean(e.target.checked))}
                  isDisabled={uploadBusy}
                >
                  Reemplazar CAF activo existente
                </Checkbox>
              </div>

              <label className="flex flex-col gap-1 text-sm font-medium text-navy-700 dark:text-gray-200">
                Motivo de reparación / contingencia
                <textarea
                  value={forceReason}
                  onChange={(e) => setForceReason(e.target.value)}
                  rows={3}
                  placeholder="Ej: CAF previo cargado en ambiente equivocado; reparar secuencia operativa."
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-800 outline-none transition focus:border-orange-300 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  disabled={uploadBusy}
                />
              </label>

              {repairValidationError ? (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>{repairValidationError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : null}
        </Box>
      ) : null}

      {profilesLoadState === "loading" && preview?.ok ? (
        <Text fontSize="sm" color="gray.500" mb={3}>
          Cargando perfiles de emisión para el RUT del CAF…
        </Text>
      ) : null}

      {profilesForRut.length > 1 ? (
        <label className="mb-4 flex flex-col gap-1 text-sm font-medium text-navy-700 dark:text-gray-200">
          Perfil de emisión (obligatorio si hay varios para este RUT)
          <select
            value={pickedInternalId}
            onChange={(e) => setPickedInternalId(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
            disabled={uploadBusy}
          >
            <option value="">— Elegí un perfil —</option>
            {profilesForRut.map((p) => (
              <option key={p.internal_id || p.id} value={p.internal_id || ""}>
                {(p.profile_name || p.internal_id || `Perfil ${p.id}`) +
                  ` — ${p.ambiente === "produccion" ? "Producción" : "Certificación"}`}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {profilesLoadState === "ready" && profilesForRut.length === 0 ? (
        <Alert status="warning" borderRadius="md" fontSize="sm" mb={3}>
          <AlertIcon />
          <AlertDescription>
            No hay perfiles activos listados para este RUT. Si tu organización tiene perfil en otro
            formato de RUT, corregilo o creá el perfil; el servidor igual intentará resolver por
            organización + RUT del XML.
          </AlertDescription>
        </Alert>
      ) : null}

      <Text fontSize="sm" color="gray.600" className="dark:text-gray-400" mb={2}>
        Elegí el archivo <strong>.xml</strong> del CAF. El sistema identifica
        el <strong>tipo de DTE (TD)</strong>, el <strong>RUT emisor (RE)</strong> y el
        rango de folios <strong>D–H</strong> leyendo el propio CAF, igual que el
        backend.
      </Text>
      <Text fontSize="sm" color="gray.600" className="dark:text-gray-400" mb={4}>
        Para <strong>descargar folios y generar el CAF</strong> tenés que ingresar al
        portal del SII (certificación), iniciar sesión y solicitar folios:{" "}
        <Link
          href={SII_SOLICITA_FOLIOS_URL}
          isExternal
          color="brand.500"
          fontWeight="600"
          textDecoration="underline"
        >
          maullin.sii.cl — solicitud de folios
        </Link>
        . Con el XML descargado, volvé acá y subilo con el botón de abajo.
      </Text>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xml,application/xml,text/xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          readFile(f || null);
        }}
      />
      <Button
        size="sm"
        variant="outline"
        leftIcon={<HiOutlineUpload />}
        onClick={() => fileInputRef.current?.click()}
        isDisabled={uploadBusy}
        mb={4}
      >
        Elegir archivo XML…
      </Button>

      {file ? (
        <Text fontSize="xs" color="gray.500" mb={2}>
          Archivo: <strong>{file.name}</strong>
        </Text>
      ) : null}

      {parseError ? (
        <Alert status="error" borderRadius="md" fontSize="sm" mb={3}>
          <AlertIcon />
          <AlertDescription>{parseError}</AlertDescription>
        </Alert>
      ) : null}

      {uploadError ? (
        <Alert status="error" borderRadius="md" fontSize="sm" mb={3}>
          <AlertIcon />
          <AlertDescription whiteSpace="pre-wrap">{uploadError}</AlertDescription>
        </Alert>
      ) : null}

      {preview?.ok ? (
        <Box
          rounded="lg"
          borderWidth="1px"
          borderColor="gray.200"
          bg="gray.50"
          p={4}
          className="dark:border-white/10 dark:bg-navy-900/40"
        >
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wider"
            color="gray.500"
            mb={3}
          >
            Identificación desde el CAF
          </Text>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500 dark:text-gray-400">Tipo DTE</dt>
              <dd className="text-right font-semibold text-navy-700 dark:text-white">
                {preview.tipoDte}{" "}
                <span className="font-normal text-gray-600 dark:text-gray-300">
                  — {labelTipoDte(preview.tipoDte)}
                </span>
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500 dark:text-gray-400">RUT emisor</dt>
              <dd className="font-mono text-navy-700 dark:text-white">
                {preview.rutEmisor}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500 dark:text-gray-400">Rango folios</dt>
              <dd className="tabular-nums font-semibold text-navy-700 dark:text-white">
                {preview.folioDesde} → {preview.folioHasta}
              </dd>
            </div>
            {preview.fechaAutorizacion ? (
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500 dark:text-gray-400">
                  Fecha autorización (FA)
                </dt>
                <dd className="text-navy-700 dark:text-white">
                  {preview.fechaAutorizacion}
                </dd>
              </div>
            ) : null}
          </dl>
        </Box>
      ) : null}
    </AppModal>
  );
}
